import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  apiTrustWishShow,
  apiTrustWishVerify,
  apiTrustWishResendOtp,
} from "../lib/api";

export default function TrustWishVerify() {
  const { wishId } = useParams<{ wishId: string }>();
  const nav = useNavigate();

  // Safely parse wishId → number or null
  const idNum = React.useMemo(() => {
    if (!wishId) return null;
    const parsed = Number.parseInt(wishId, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [wishId]);

  const [code, setCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    // If the URL is broken and we don't have a valid ID, show error instead of calling API with NaN
    if (idNum === null) {
      setErr("Invalid wish reference. Please use the latest link from your email.");
      return;
    }

    (async () => {
      try {
        const r = await apiTrustWishShow(idNum);
        if (r?.wish?.otp_verified_at) {
          // basename="/app" — never include "/app" in SPA route strings
          nav(`/trusts/wishes/${idNum}`, { replace: true });
        }
      } catch {
        // ignore — let the user enter OTP
      }
    })();
  }, [idNum, nav]);

  async function onVerify() {
    setErr(null);
    setInfo(null);

    if (idNum === null) {
      setErr("Invalid wish reference. Please open the latest verification link.");
      return;
    }

    if (code.length !== 6) {
      setErr("Enter the 6-digit code");
      return;
    }

    setLoading(true);
    try {
      // If the request fails (4xx/5xx), apiTrustWishVerify should throw.
      // So if we get here without an exception, treat it as success.
      await apiTrustWishVerify(idNum, { code });

      // Optional info message (user might not see it long because of redirect)
      setInfo("Wish confirmed successfully.");

      // Go to wish details page
      nav(`/trusts/wishes/${idNum}`, { replace: true });
    } catch (e: any) {
      setErr(e?.message || "Could not verify code");
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    setErr(null);
    setInfo(null);

    if (idNum === null) {
      setErr("Invalid wish reference. Please open the latest verification link.");
      return;
    }

    try {
      await apiTrustWishResendOtp(idNum);
      setInfo("New code sent.");
    } catch {
      setErr("Could not resend code. Try again.");
    }
  }

  return (
    <div className="section-box max-w-sm mx-auto p-4 sm:p-6">
      <h1 className="text-xl font-semibold mb-2">Confirm Your Wish</h1>
      <p className="text-sm text-gray-600 mb-4">
        We sent a 6-digit code to your email. Enter it below.
      </p>

      {err && <div className="alert-danger mb-3">{err}</div>}
      {info && <div className="alert-success mb-3">{info}</div>}

      <div className="space-y-4">
        <input
          className={[
            "input w-full text-center text-2xl tracking-widest",
            "rounded-xl border border-gray-300 px-3 py-3",
            "focus:outline-none focus:ring-2 focus:ring-blue-500",
          ].join(" ")}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="••••••"
          disabled={loading || idNum === null}
        />

        <div className="flex gap-2">
          <button
            className="btn btn-success w-auto px-4"
            onClick={onVerify}
            disabled={loading || idNum === null}
          >
            {loading ? "Verifying…" : "Verify"}
          </button>
          <button
            className="btn btn-white w-auto px-4"
            onClick={onResend}
            disabled={loading || idNum === null}
          >
            Resend code
          </button>
        </div>
      </div>
    </div>
  );
}
